import type { ScaleOrdinal } from 'd3';
import { scaleBand, scaleLinear } from 'd3';
import { clamp } from 'lodash';
import type { JSX } from 'react';

import type { FormatType } from '../../types';
import { getFormatter, getMaxTextWidth, useChartTheme } from '../../utilities';
import { AxisLabel } from '../axis/axis-label';
import type { BarData } from '../bar/bar-chart';
import { BarLabel } from '../bar/bar-label';
import {
  calculateGroupLayout,
  collectBarLabels,
  Dimensions,
  type DisplayBar,
  type DisplayGroup,
  GroupedBarLegend,
  getMaxBarCount,
  getZeroedExtent,
  HoverableBar,
} from './grouped-bar-shared';

type HorizontalGroupedBarProps<TData extends object> = {
  displayData: DisplayGroup<TData>[];
  colorScale: ScaleOrdinal<string, string>;
  width: number;
  height: number;
  hoveredSeries: string | null;
  setHoveredSeries: (label: string | null) => void;
  format: FormatType;
  showBarLabels: boolean;
  hideLegend: boolean;
  formatLegendLabel: (label: string) => string;
  maxLegendLabelWidth?: number;
  tooltipContent?: (group: string, bar: BarData<TData>) => JSX.Element;
};

export const HorizontalGroupedBar = <TData extends object>({
  displayData,
  colorScale,
  width,
  height,
  hoveredSeries,
  setHoveredSeries,
  format,
  showBarLabels,
  hideLegend,
  formatLegendLabel,
  maxLegendLabelWidth,
  tooltipContent,
}: HorizontalGroupedBarProps<TData>) => {
  const { isNewTheme } = useChartTheme();
  const barCountPerGroup = getMaxBarCount(displayData);
  const minPlotHeight =
    displayData.length * Dimensions.MinBarHeight * barCountPerGroup;

  // Single bar per group makes legend redundant with axis labels.
  const shouldHideLegend =
    hideLegend ||
    barCountPerGroup <= 1 ||
    minPlotHeight + Dimensions.LegendHeight > height;
  const legendHeight = shouldHideLegend ? 0 : Dimensions.LegendHeight;

  const labelWidth = clamp(
    width * Dimensions.MaxLabelWidthRatio,
    Dimensions.MinLabelWidth,
    Dimensions.MaxLabelWidth
  );

  const plotInnerWidth = Math.max(
    0,
    width - labelWidth - Dimensions.BarEdgePadding
  );
  const plotHeight = Math.max(
    height - legendHeight - Dimensions.HorizontalChartStackGap,
    minPlotHeight
  );
  const chartInsetLeft = labelWidth + Dimensions.BarEdgePadding;
  const isPlotSizeValid = plotInnerWidth > 0 && plotHeight > 0;

  const barLabels = collectBarLabels(displayData);
  const allValues = displayData
    .flatMap((d) => d.values.map((v) => v.value))
    .filter((v): v is number => v !== null);
  const formatValue = getFormatter(format);
  const totalBarLabelWidth = getMaxTextWidth({
    texts: allValues.map((value) => formatValue(value)),
  });
  const maxBarLabelSize =
    plotInnerWidth * Dimensions.HorizontalMaxBarLabelWidthRatio -
    Dimensions.BarLabelPadding;
  const barLabelSize = Math.min(
    Math.max(Dimensions.HorizontalMinBarLabelSize, totalBarLabelWidth),
    Math.max(0, maxBarLabelSize)
  );
  const valueSpan = Math.max(
    0,
    plotInnerWidth -
      (showBarLabels ? barLabelSize + Dimensions.BarLabelPadding : 0)
  );

  const groupScale = scaleBand<string>()
    .domain(displayData.map((d) => d.group))
    .range([0, plotHeight])
    .paddingInner(Dimensions.HorizontalBandPadding)
    .paddingOuter(Dimensions.HorizontalBandPadding);

  // New theme thins the bars and inserts a small gap between them (Figma
  // "Dashboard 2026"); legacy keeps the original flush, full-bandwidth bars.
  const barScale = scaleBand()
    .domain(barLabels)
    .range([0, groupScale.bandwidth()])
    .paddingInner(isNewTheme ? Dimensions.HorizontalBarBandPaddingInner : 0);

  const zeroedExtent = getZeroedExtent(displayData);
  const valueScale = zeroedExtent
    ? scaleLinear([zeroedExtent.min, zeroedExtent.max], [0, valueSpan])
    : null;

  const groupByName = new Map(displayData.map((g) => [g.group, g]));

  const renderBar = (bar: DisplayBar<TData>, index: number, group: string) => {
    if (bar.value === null || !valueScale) return null;

    const barThickness = barScale.bandwidth();
    const barY = index * barScale.step();
    const x0 = valueScale(0);
    const x1 = valueScale(bar.value);
    const x = Math.min(x0, x1);
    const barWidth = Math.abs(x1 - x0);
    const labelX = Math.max(x0, x1) + Dimensions.BarLabelPadding;
    const labelSlotWidth = plotInnerWidth - labelX;
    const labelWidth = Math.min(labelSlotWidth, barLabelSize);

    return (
      <HoverableBar
        key={bar.label}
        bar={bar}
        value={bar.value}
        group={group}
        format={format}
        fill={bar.color || colorScale(bar._colorKey)}
        rect={{ x, y: barY, width: barWidth, height: barThickness }}
        hoveredSeries={hoveredSeries}
        setHoveredSeries={setHoveredSeries}
        tooltipContent={tooltipContent}
      >
        {showBarLabels && labelWidth > 0 && (
          <BarLabel
            x={labelX}
            y={barY}
            width={labelWidth}
            height={barThickness}
            label={formatValue(bar.value)}
          />
        )}
      </HoverableBar>
    );
  };

  const renderGroup = (group: DisplayGroup<TData>) => {
    const { primaryOffset } = calculateGroupLayout(
      group.values.length,
      barScale,
      groupScale.bandwidth()
    );
    const groupPos = groupScale(group.group);
    if (groupPos === undefined) return null;

    return (
      <g
        key={group.group}
        transform={`translate(0, ${groupPos + primaryOffset})`}
        data-testid="grouped-bar-chart-group"
      >
        {group.values.map((bar, index) => renderBar(bar, index, group.group))}
      </g>
    );
  };

  const renderAxisLabel = (domain: string) => {
    const y = groupScale(domain);
    const group = groupByName.get(domain);
    if (y === undefined || !group) return null;

    const { groupSpan, primaryOffset } = calculateGroupLayout(
      group.values.length,
      barScale,
      groupScale.bandwidth()
    );

    return (
      <AxisLabel
        key={domain}
        y={y + primaryOffset + groupSpan / 2}
        width={labelWidth}
        fontSize={11}
        label={domain}
        availableHeight={groupSpan}
        testId="grouped-bar-chart-axis-label"
      />
    );
  };

  return (
    <div style={{ width: '100%' }}>
      <svg width="100%" height={plotHeight}>
        <g id="y-axis">
          {isPlotSizeValid && groupScale.domain().map(renderAxisLabel)}
        </g>
        <g id="chart" transform={`translate(${chartInsetLeft}, 0)`}>
          {isPlotSizeValid && valueScale && displayData.map(renderGroup)}
        </g>
      </svg>
      {!shouldHideLegend && isPlotSizeValid && (
        <GroupedBarLegend
          barLabels={barLabels}
          displayData={displayData}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
          marginLeft={chartInsetLeft}
        />
      )}
    </div>
  );
};
